/**
 * 編集中の表示（#202、docs/82 §4）。見積書・請求書の見出しのチップと、スマホの見積の頭。
 *   ・直せる帳票（下書き・訂正中）を開いていて、タブが見えている間だけ 30 秒ごとに合図（POST /documents/:id/editing）を送る。
 *     隠れたら止め、見えたらすぐ1回。離れるときは消す（DELETE。ページを閉じる・ほかへ移るときは navigator.sendBeacon の POST …/editing/leave）
 *   ・合図は人の操作でない＝PASSIVE（X-User-Action を付けない＝自動ログアウトの操作に数えない）。断られても何もしない（表示だけ）
 *   ・答えの「自分以外の編集中」を「山田 太郎・佐藤 花子が編集中」のチップに。保存は止めない
 */
import { EDITING_BEAT_MS, editingLabel, type DocumentEditor, type DocumentEditors } from "@genbaryoku/contract";
import { useEffect, useState } from "react";
import { api } from "../api.js";
import { PASSIVE } from "../lib/autoLogout.js";
import { productHeaders } from "../lib/product.js";

/**
 * 合図・消すを1本ずつ送る（StrictMode の付け外しで「消す」が「合図」のあとに届き、行を消してしまわないように。送った順に届く）
 */
let chain: Promise<unknown> = Promise.resolve();
const queue = <T,>(f: () => Promise<T>): Promise<T> => {
  const p = chain.then(f);
  chain = p.catch(() => undefined);
  return p;
};

export function useEditors(docId: string | undefined, active: boolean): DocumentEditor[] {
  const [editors, setEditors] = useState<DocumentEditor[]>([]);
  useEffect(() => {
    if (!docId || !active) {
      setEditors([]);
      return;
    }
    let stopped = false;
    let timer: number | undefined;
    const beat = async () => {
      window.clearTimeout(timer);
      if (stopped || document.hidden) return; // 隠れている間は送らない（見えたら onVisible がすぐ送る）
      try {
        const r = await queue(() => api<DocumentEditors>("POST", `/documents/${docId}/editing`, {}, PASSIVE));
        if (!stopped) setEditors(r.editors);
      } catch {
        /* 表示だけ。断られても何もしない（次の合図でもう一度） */
      }
      if (!stopped) timer = window.setTimeout(() => void beat(), EDITING_BEAT_MS);
    };
    const onVisible = () => {
      if (document.hidden) window.clearTimeout(timer);
      else void beat();
    };
    // ページを閉じる・ほかへ移る（画面の後片づけは走らない）：sendBeacon で消す（Safari でもページが消えても届く。無ければ keepalive の fetch）。
    // 戻ってきたら（bfcache）見えた合図でまた送る。送れなくても 90 秒で消える
    const onHide = () => {
      window.clearTimeout(timer);
      const url = `/api/v1/documents/${docId}/editing/leave`;
      try {
        if (typeof navigator.sendBeacon === "function" && navigator.sendBeacon(url)) return;
        void fetch(url, { method: "POST", credentials: "include", keepalive: true, headers: productHeaders() }).catch(() => undefined);
      } catch {
        /* 送れなくても 90 秒で消える */
      }
    };
    void beat();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pagehide", onHide);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pagehide", onHide);
      void queue(() => api("DELETE", `/documents/${docId}/editing`, undefined, PASSIVE)).catch(() => undefined);
    };
  }, [docId, active]);
  return editors;
}

/** 見出しのチップ「〇〇が編集中」（自分以外がいなければ何も描かない） */
export function EditingChip({ editors }: { editors: readonly DocumentEditor[] }) {
  const label = editingLabel(editors.map((e) => e.name));
  if (!label) return null;
  return (
    <span className="chip editing" title={label} data-testid="editing-chip">
      {label}
    </span>
  );
}
